import { HeadContent, Outlet, Scripts, createRootRouteWithContext } from '@tanstack/react-router';
import type { AnyContext, ErrorComponentProps, Register } from '@tanstack/react-router';
import { QueryClientProvider } from '@tanstack/react-query';
import { lazy, Suspense, useEffect, useState } from 'react';
import type { ReactNode } from 'react';

import { ErrorState } from '@/components/v2/error-state';
import { v2 } from '@/lib/i18n/v2';
import { LaunchShell } from '@/components/v2/logo';
import { MotionProvider } from '@/components/motion-provider';
import { PwaSplash } from '@/components/splash/pwa-splash';
import { OnlineStatusBanner } from '@/components/shared/online-status-banner/online-status-banner';
import { MobilePrompt } from '@/components/layout/mobile-prompt/mobile-prompt';
import { useSmartBack } from '@/lib/navigation/smartBack';

const LazyNotificationToaster = lazy(() =>
  import('@/components/notifications/notification-toaster').then(({ NotificationToaster }) => ({
    default: NotificationToaster,
  }))
);
// Import font CSS files (served from /fonts/ in public directory)
import '@/styles/fonts.css';

import '../styles/index.less';
import { PWAUpdatePrompt } from '@/components/shared/prompt/PWAUpdatePrompt';
import styles from './__root.module.less';
import { API_CONFIG } from '@/lib/config/api.config';
import { AuthProvider, type AuthState } from '@/contexts/AuthContext';
import { AuthenticatedWarmStart } from '@/components/warm-start/warm-start';
import { DevSkeletonProvider } from '@/lib/boneyard/dev-toggle';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { getAssetsBaseUrl, getAssetUrl, ASSET_PATHS } from '@/lib/utils/assets';
import { getSiteOrigin } from '@/lib/utils/site-meta';
import { getCsrfToken, setCsrfToken } from '@/lib/auth/csrfToken';
import { logger } from '@/lib/utils/logger';
import { useSafeArea } from '@/hooks/ui/useSafeArea';
import { useTokenRefresh } from '@/hooks/core/useTokenRefresh';
import { useAuthIdentitySync } from '@/hooks/core/useAuthIdentitySync';
import {
  loadRootSessionContextForMatch,
  type RootRouterContext,
} from '@/lib/auth/root-session-context';
import { stampCspNonce } from '@/lib/ssr/csp-nonce';

/**
 * Root Error Component
 * Global error boundary for unhandled errors
 * Uses ErrorFallback for consistent error styling
 */
function RootErrorComponent({ error }: ErrorComponentProps) {
  return (
    <ErrorState
      severity="crash"
      page
      error={error instanceof Error ? error : undefined}
      onRetry={() => {
        window.location.reload();
      }}
      onHome={() => {
        window.location.href = '/';
      }}
    />
  );
}

/** Root Pending Component shown during initial route load. */
function RootPendingComponent() {
  return <LaunchShell />;
}

/**
 * Root Not Found Component
 * Shown when route is not found — the standard ErrorPage with 404 copy.
 */
function RootNotFoundComponent() {
  // Pop history when the 404 was reached in-app; cold-opened 404 links have no
  // in-app history to pop, and a bare window.history.back() would leave the
  // site entirely — replace to home instead.
  const smartBackToHome = useSmartBack({ to: '/' });
  return (
    <ErrorState
      severity="not-found"
      page
      title={v2.error.notFound('this page')}
      onGoBack={smartBackToHome}
      onHome={() => window.location.assign('/')}
    />
  );
}

// Home-screen icon cache-busting (#324): the CDN caches `/*.png` for 30 days
// (see public/_headers), so #340's regenerated (centred ~70%) icons stayed
// hidden behind the old edge-to-edge PNGs at the same URLs. #347 tried a `?v=`
// query string, but this SSR deploy 500s on ANY static asset with a query
// string (the query bypasses static serving and falls through to a route
// handler) — so iOS/Chrome fetched a 500 and registered NO icon at all. iOS
// also ignores query strings on apple-touch-icon regardless. The correct,
// iOS-safe bust is a NEW FILENAME (path change) → fresh CDN entry, no query
// string. Bump the `-vN` suffix on the /favicon home-screen PNGs when they
// change. LIMIT: this cannot update an icon iOS has ALREADY rendered to a home
// screen — the user must remove and re-add the shortcut to pick up the new one.

/**
 * Root Route Configuration
 * ✅ SSR MIGRATION: Fetches user session server-side for immediate auth state
 */
// TBeforeLoadFn is pinned explicitly: 1.170.41 routes the root beforeLoad through
// the SSR-serializability Constrain, whose inference collapses to a `never`
// return and erases the session/authState context enrichment downstream.
export const Route = createRootRouteWithContext<RootRouterContext>()<
  Register,
  AnyContext,
  typeof loadRootSessionContextForMatch
>({
  // Stamps the per-request CSP nonce before the SSR stream renders (issue #638).
  server: { middleware: [stampCspNonce] },
  // The root match is the single session authority for every navigation.
  beforeLoad: ({ context, preload }) => loadRootSessionContextForMatch(context, preload),
  head: () => ({
    meta: [
      {
        charSet: 'utf-8',
      },
      {
        name: 'viewport',
        content:
          'width=device-width, initial-scale=1, viewport-fit=cover, interactive-widget=resizes-content',
      },
      {
        title: 'TIL Dance Singapore',
      },
      {
        name: 'description',
        content: "Discover Singapore's best dance classes and studios",
      },
      // Static OG/Twitter defaults (audit UIUX-02): the share sheet targets
      // Telegram/WhatsApp/LINE/WeChat/SMS, all of which scrape these tags to
      // render link previews. Entity routes override og:url/og:title etc.
      {
        property: 'og:site_name',
        content: 'TIL Dance Singapore',
      },
      {
        property: 'og:type',
        content: 'website',
      },
      {
        property: 'og:url',
        content: getSiteOrigin(),
      },
      {
        property: 'og:title',
        content: 'TIL Dance Singapore',
      },
      {
        property: 'og:description',
        content: "Discover Singapore's best dance classes and studios",
      },
      {
        property: 'og:image',
        content: getAssetUrl(ASSET_PATHS.SINGAPORE_BACKDROP),
      },
      {
        name: 'twitter:card',
        content: 'summary_large_image',
      },
      {
        name: 'apple-mobile-web-app-capable',
        content: 'yes',
      },
      {
        name: 'apple-mobile-web-app-status-bar-style',
        content: 'black-translucent',
      },
      {
        name: 'theme-color',
        content: '#6366f1',
      },
    ],
    // Set CSS custom properties for asset URLs (used by globals.less)
    styles: [
      {
        children: `:root { --assets-base-url: '${getAssetsBaseUrl()}'; --desktop-bg-url: url('${getAssetUrl('/placeholders/homepage/singapore-backdrop.webp')}'); }`,
      },
    ],
    scripts: [
      {
        // UIUX-14: theme used to apply in a post-paint effect, so returning
        // dark users got a light flash on every cold load. Reads the same
        // localStorage key ThemeContext persists and sets the root attrs
        // before first paint; the effect stays the source of truth after.
        children: `(function(){try{var raw=localStorage.getItem('til-base-theme');var t=raw?JSON.parse(raw):'light';var d=String(t).indexOf('dark')!==-1;var r=document.documentElement;r.className='theme-'+t;r.setAttribute('data-theme',d?'dark':'light');if(d)r.classList.add('dark');}catch(e){}})();`,
      },
    ],
    links: [
      {
        rel: 'apple-touch-icon',
        sizes: '180x180',
        href: '/favicon/apple-touch-icon-v3.png',
      },
      {
        rel: 'icon',
        type: 'image/png',
        sizes: '32x32',
        href: '/favicon/favicon-32x32.png',
      },
      {
        rel: 'icon',
        type: 'image/png',
        sizes: '16x16',
        href: '/favicon/favicon-16x16.png',
      },
      {
        rel: 'manifest',
        href: '/manifest.webmanifest',
      },
      // ⚡ PERFORMANCE: Preconnect to critical domains for faster resource loading
      // Preconnect to API - saves ~500-1000ms for first API request
      { rel: 'preconnect', href: API_CONFIG.BASE_URL },
      // Preload the two above-the-fold font weights (headings + body). With
      // font-display: swap the CSS-triggered fetch chain otherwise pops text
      // in late on cold loads.
      {
        rel: 'preload',
        as: 'font',
        type: 'font/woff2',
        href: '/fonts/fredoka-latin-500-normal.woff2',
        crossOrigin: 'anonymous',
      },
      {
        rel: 'preload',
        as: 'font',
        type: 'font/woff2',
        href: '/fonts/plus-jakarta-sans-latin-400-normal.woff2',
        crossOrigin: 'anonymous',
      },
      // DNS prefetch for image CDN and other assets (environment-conscious)
      { rel: 'dns-prefetch', href: getAssetsBaseUrl() },
      { rel: 'dns-prefetch', href: 'https://api.til.sg' },
    ],
  }),
  errorComponent: RootErrorComponent,
  pendingComponent: RootPendingComponent,
  notFoundComponent: RootNotFoundComponent,
  component: RootComponent,
});

function RootComponent() {
  return (
    <RootDocument>
      <Outlet />
    </RootDocument>
  );
}

/**
 * Root Document Component
 *
 * ✅ SSR MIGRATION: Session is now loaded server-side in root loader
 * - QueryClient is created independently using getQueryClient()
 * - Server-side: Fresh QueryClient per request
 * - Client-side: Singleton QueryClient reused across navigations
 * - No SSR serialization issues since QueryClient is NOT in router context
 * - Session data is immediately available from loader context
 *
 * @see https://tanstack.com/start/latest/docs/framework/react/ssr
 * @see https://tanstack.com/query/latest/docs/framework/react/guides/ssr
 */
function RootDocument({ children }: Readonly<{ children: ReactNode }>) {
  // ✅ Use the per-request QueryClient from router context — the SAME instance the
  // router's dehydrate hook serializes. A separate getQueryClient() on the server
  // would create a different instance and break hydration.
  const { queryClient, session, authState } = Route.useRouteContext();

  // Session data is already in React Query before render. This effect only
  // restores or initializes the CSRF token for the active session.
  useEffect(() => {
    // Skip on server-side
    if (typeof window === 'undefined') {
      return;
    }

    // ✅ Restore CSRF token from sessionStorage if available
    const csrfToken = getCsrfToken();
    if (csrfToken) {
      logger.info('RootDocument', 'CSRF token restored from sessionStorage');
    }

    // If we have a session, ensure we have a CSRF token
    if (session) {
      // ✅ Optimization: Use CSRF token from SSR session if available
      if (session.csrf_token) {
        setCsrfToken(session.csrf_token);
        logger.info('RootDocument', 'CSRF token hydrated from SSR session');
        return;
      }

      // Fallback: Fetch CSRF token if not in session
      // A restored sessionStorage token already covers this session, so skip
      // the redundant network fetch — same early return as
      // apiClient.ensureCsrfToken. The SSR-hydration branch above still wins:
      // a fresh session.csrf_token is newer than whatever we had stored.
      if (csrfToken) return;

      // INTENTIONALLY using fetch() instead of TanStack Query:
      // - This is a one-time initialization side-effect, not data fetching
      // - CSRF tokens must NOT be cached - they need to be fresh per session
      // - Response is used imperatively (extract from header, store in sessionStorage)
      // - TanStack Query is for managing server state, not for initialization side-effects
      const ensureCsrfToken = async () => {
        try {
          const response = await fetch(`${API_CONFIG.BASE_URL}/v1/auth/session`, {
            method: 'GET',
            credentials: 'include',
          });

          if (response.ok) {
            const csrfTokenFromHeader = response.headers.get('X-CSRF-Token');
            if (csrfTokenFromHeader) {
              setCsrfToken(csrfTokenFromHeader);
              logger.info('RootDocument', 'CSRF token updated from session response');
            }
          }
        } catch (error) {
          logger.warn('RootDocument', 'CSRF token fetch failed', { error });
        }
      };

      ensureCsrfToken();
    }
  }, [authState, queryClient, session]);

  // Warm the dotlottie runtime off the critical path. Lazy surfaces (favorite
  // button, social cards, reactions, confetti) mount players via a lazy
  // wrapper; one idle import spares their first interaction the ~168 KB
  // runtime fetch. The 1.2 MB wasm stays on-demand (too heavy to
  // speculatively fetch on mobile data).
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const idle = (
      window as unknown as {
        requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      }
    ).requestIdleCallback;
    const warm = () => void import('@lottiefiles/dotlottie-react').catch(() => undefined);
    if (idle) idle(warm, { timeout: 5000 });
    else window.setTimeout(warm, 3000);
  }, []);

  return (
    <html lang="en" suppressHydrationWarning>
      <head>
        <HeadContent />
      </head>
      <body className={styles.rootDocument}>
        <div className={styles.rootDocumentAppContainer} data-scroll-root>
          <QueryClientProvider client={queryClient}>
            {/* a11y: single <main> landmark wraps all routed page content so every
                document (home, login, 404) has exactly one main. Overlays stay
                siblings, outside main.
                Fixes Lighthouse landmark-one-main. */}
            <AppProviders initialAuthState={authState}>
              {/* a11y (UIUX-27): keyboard users had to tab through the sticky
                  discovery bar on every page; the skip link jumps straight to
                  the single main landmark. */}
              <a href="#main-content" className={styles.skipLink}>
                Skip to content
              </a>
              <main id="main-content">{children}</main>
            </AppProviders>
          </QueryClientProvider>
        </div>
        {/* PWA launch splash: sibling of the app container so its fixed
            overlay is not trapped by the container's stacking context; SSR
            renders it before <Scripts />, painting the wordmark pre-hydration. */}
        <PwaSplash />
        <Scripts />
      </body>
    </html>
  );
}

/**
 * App Providers Component
 * Wraps all context providers and enables hooks that depend on QueryClient
 * Must be rendered INSIDE QueryClientProvider to access query client context
 */
function AppProviders({
  children,
  initialAuthState,
}: Readonly<{ children: ReactNode; initialAuthState?: AuthState }>) {
  // Initialize safe area handling for Telegram Mini Apps
  useSafeArea();

  return (
    <ThemeProvider>
      <AuthProvider initialAuthState={initialAuthState}>
        {/* useTokenRefresh → useSession → useAuth, so it needs Auth context.
            Calling it directly in AppProviders throws "useAuth must be used
            within an AuthProvider" because <AuthProvider> is this component's
            child, not its ancestor. Hosted by a null-rendering child inside
            the provider instead. */}
        <TokenRefreshManager />
        <AuthIdentitySyncManager />
        {/* Warm the nav-adjacent primary tabs (chunk + loader data) once the
            session is established, then the rest of the authed route graph as
            code only: since #1241 made the tabs lazy routes, first taps were
            paying both the chunk fetch and a cold API fetch, and every
            subpage whose entry point is not a viewport-visible card was paying
            its chunk download plus parse at tap time. */}
        <AuthenticatedWarmStart />
        <DeferredNotificationToaster />
        <OnlineStatusBanner />
        <MobilePrompt />
        <MotionProvider>
          <DevSkeletonProvider>{children}</DevSkeletonProvider>
          <PWAUpdatePrompt />
        </MotionProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

// Renders nothing — exists so useTokenRefresh runs inside <AuthProvider>
// (auth context) while still under RootDocument's QueryClientProvider.
function TokenRefreshManager() {
  useTokenRefresh();
  return null;
}

// Renders nothing — mounts the once-per-tab auth identity watcher so a
// logout / identity change in ANY tab wipes this tab's private cache too (#866).
function AuthIdentitySyncManager() {
  useAuthIdentitySync();
  return null;
}

function DeferredNotificationToaster() {
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => setHydrated(true), []);
  if (!hydrated) return null;
  return (
    <Suspense fallback={null}>
      <LazyNotificationToaster />
    </Suspense>
  );
}
